import { useEffect, useState } from "react";
import useInView from "@/hooks/useInView";
import { Tag, Sparkles, ShieldCheck, Wallet } from "lucide-react";

const floatShadow = "0 1px 2px rgba(0,0,0,0.04), 0 6px 16px rgba(0,0,0,0.06)";

function GraphicPricing({ animate }) {
  const [rate, setRate] = useState(0);
  useEffect(() => {
    if (!animate) { setRate(0); return; }
    let start = null;
    const target = 189;
    const raf = (ts) => {
      if (start === null) start = ts;
      const p = Math.min((ts - start) / 900, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setRate(Math.round(eased * target));
      if (p < 1) requestAnimationFrame(raf);
    };
    const id = requestAnimationFrame(raf);
    return () => cancelAnimationFrame(id);
  }, [animate]);

  return (
    <div style={{ marginTop: "auto", paddingTop: 18, marginBottom: -1 }}>
      <div style={{ padding: "0 0 18px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 10 }}>
          {["Airbnb", "Booking.com", "Vrbo", "+13"].map((p, i) => (
            <span key={p} style={{ fontSize: 9.5, fontWeight: 600, fontFamily: "'Inter', sans-serif", color: i === 3 ? "#8A9B92" : "#555", background: "#EFEFED", borderRadius: 5, padding: "3px 8px" }}>{p}</span>
          ))}
        </div>
      </div>
      <div style={{ background: "#fff", borderRadius: "12px 12px 0 0", padding: "14px 18px 16px", boxShadow: floatShadow, position: "relative" }}>
        <div style={{ fontSize: 9.5, fontWeight: 600, color: "#8A9B92", fontFamily: "'Inter', sans-serif", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Tonight's rate</div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 26, color: "#112215", letterSpacing: "-0.02em" }}>&pound;{rate}</span>
          <span style={{ fontSize: 9.5, fontWeight: 700, color: "#CD533B", fontFamily: "'Inter', sans-serif", background: "#FED7C3", padding: "2px 7px", borderRadius: 100, opacity: animate ? 1 : 0, transition: "opacity 0.3s 0.7s" }}>&uarr; 12%</span>
          <span style={{ fontSize: 9, color: "#8A9B92", fontFamily: "'Inter', sans-serif", marginLeft: "auto" }}>vs last week</span>
        </div>
        {/* Mini sparkline */}
        <svg viewBox="0 0 100 16" width="100%" height={16} preserveAspectRatio="none" style={{ display: "block", marginTop: 8 }}>
          <polyline points="0,12 14,9 28,11 42,6 56,7 70,3 84,5 100,2" fill="none" stroke="#CD533B" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="200" strokeDashoffset={animate ? 0 : 200} style={{ transition: "stroke-dashoffset 1s ease-out 0.3s" }} />
        </svg>
      </div>
    </div>
  );
}

function GraphicChat({ animate }) {
  return (
    <div style={{ marginTop: "auto", paddingTop: 18, marginBottom: -1 }}>
      <div style={{ background: "#fff", borderRadius: "12px 12px 0 0", padding: "14px 18px 16px", boxShadow: floatShadow }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ background: "#F2F2F0", borderRadius: "10px 10px 10px 2px", padding: "8px 11px", alignSelf: "flex-start", maxWidth: "85%", boxShadow: "0 1px 2px rgba(0,0,0,0.03)" }}>
            <span style={{ fontSize: 10.5, fontFamily: "'Inter', sans-serif", color: "#555" }}>When's check-in?</span>
          </div>
          <div style={{ background: "#112215", borderRadius: "10px 10px 2px 10px", padding: "8px 11px", alignSelf: "flex-end", maxWidth: "85%", opacity: animate ? 1 : 0, transform: animate ? "translateY(0)" : "translateY(6px)", transition: "opacity 0.4s 0.3s, transform 0.4s 0.3s", boxShadow: "0 2px 6px rgba(0,0,0,0.12)" }}>
            <span style={{ fontSize: 10.5, fontFamily: "'Inter', sans-serif", color: "#fff" }}>From 3pm. Code sent.</span>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 10, opacity: animate ? 1 : 0, transition: "opacity 0.3s 0.7s" }}>
          <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#CD533B" }} />
          <span style={{ fontSize: 9.5, fontWeight: 600, color: "#5C7268", fontFamily: "'Inter', sans-serif" }}>Avg reply: 2 min</span>
        </div>
      </div>
    </div>
  );
}

function GraphicCleaning({ animate }) {
  const steps = [
    { label: "Clean completed", time: "11:15am", done: true },
    { label: "Inspection", time: "No issues", done: true },
    { label: "Ready for guests", time: "3:00pm", done: false },
  ];
  return (
    <div style={{ marginTop: "auto", paddingTop: 18, marginBottom: -1 }}>
      <div style={{ background: "#fff", borderRadius: "12px 12px 0 0", padding: "14px 18px 16px", boxShadow: floatShadow }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 0, position: "relative" }}>
          {steps.map((s, i) => (
            <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: i < steps.length - 1 ? 14 : 0, position: "relative", opacity: animate ? 1 : 0, transform: animate ? "translateX(0)" : "translateX(-6px)", transition: `opacity 0.35s ${i * 0.15}s, transform 0.35s ${i * 0.15}s` }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", border: s.done ? "none" : "1.5px solid #D5D5D5", background: s.done ? "#CD533B" : "transparent", flexShrink: 0, zIndex: 1 }} />
              {i < steps.length - 1 && <div style={{ position: "absolute", left: 4, top: 14, bottom: -4, width: 2, background: "#EAEAEA" }} />}
              <span style={{ flex: 1, fontSize: 11, color: "#112215", fontFamily: "'Inter', sans-serif", fontWeight: 500 }}>{s.label}</span>
              <span style={{ fontSize: 9.5, color: "#8A9B92", fontFamily: "'Inter', sans-serif" }}>{s.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function GraphicGuestsSecurity({ animate }) {
  const checks = ["ID verified", "Deposit held"];
  return (
    <div style={{ marginTop: "auto", paddingTop: 18, marginBottom: -1 }}>
      <div style={{ background: "#fff", borderRadius: "12px 12px 0 0", padding: "14px 18px 16px", boxShadow: floatShadow }}>
        {/* Chat messages */}
        <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 12 }}>
          <div style={{ background: "#F2F2F0", borderRadius: "10px 10px 10px 2px", padding: "7px 10px", alignSelf: "flex-start", maxWidth: "85%", boxShadow: "0 1px 2px rgba(0,0,0,0.03)" }}>
            <span style={{ fontSize: 10, fontFamily: "'Inter', sans-serif", color: "#555" }}>When's check-in?</span>
          </div>
          <div style={{ background: "#112215", borderRadius: "10px 10px 2px 10px", padding: "7px 10px", alignSelf: "flex-end", maxWidth: "85%", opacity: animate ? 1 : 0, transform: animate ? "translateY(0)" : "translateY(6px)", transition: "opacity 0.4s 0.3s, transform 0.4s 0.3s", boxShadow: "0 2px 6px rgba(0,0,0,0.12)" }}>
            <span style={{ fontSize: 10, fontFamily: "'Inter', sans-serif", color: "#fff" }}>Hi! Check-in is from 3pm &#128522; Your code is on its way.</span>
          </div>
        </div>
        {/* Divider */}
        <div style={{ height: 1, background: "#EAEAEA", marginBottom: 10 }} />
        {/* Security checks */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {checks.map((label, i) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 7, opacity: animate ? 1 : 0, transform: animate ? "scale(1)" : "scale(0.7)", transition: `opacity 0.3s ${0.5 + i * 0.15}s, transform 0.3s ${0.5 + i * 0.15}s` }}>
              <span style={{ width: 12, height: 12, borderRadius: "50%", background: "#CD533B", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: "#fff", fontSize: 6, fontWeight: 800 }}>&#10003;</span>
              <span style={{ fontSize: 10, color: "#555", fontFamily: "'Inter', sans-serif" }}>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function GraphicPayments({ animate }) {
  const [payout, setPayout] = useState(0);
  useEffect(() => {
    if (!animate) { setPayout(0); return; }
    let start = null;
    const target = 2120;
    const raf = (ts) => {
      if (start === null) start = ts;
      const p = Math.min((ts - start) / 1000, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setPayout(Math.round(eased * target));
      if (p < 1) requestAnimationFrame(raf);
    };
    const id = requestAnimationFrame(raf);
    return () => cancelAnimationFrame(id);
  }, [animate]);

  const vals = [40, 52, 47, 63, 78, 92];
  const w = 100, h = 34, max = 100;
  const coords = vals.map((v, i) => [(i / (vals.length - 1)) * w, h - (v / max) * h]);
  const linePath = `M${coords.map((c) => c.join(",")).join(" L")}`;
  const areaPath = `${linePath} L${w},${h} L0,${h} Z`;

  return (
    <div style={{ marginTop: "auto", paddingTop: 18, marginBottom: -1 }}>
      <div style={{ background: "#fff", borderRadius: "12px 12px 0 0", padding: "14px 18px 16px", boxShadow: floatShadow }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <span style={{ fontSize: 9.5, fontWeight: 600, color: "#8A9B92", fontFamily: "'Inter', sans-serif", textTransform: "uppercase", letterSpacing: "0.06em" }}>Next payout</span>
          <span style={{ fontSize: 8.5, fontWeight: 700, color: "#CD533B", fontFamily: "'Inter', sans-serif", background: "#FED7C3", padding: "2px 7px", borderRadius: 100 }}>Paid</span>
        </div>
        <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 22, color: "#112215", marginBottom: 8, letterSpacing: "-0.02em" }}>&pound;{payout.toLocaleString("en-GB")}</div>
        <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} preserveAspectRatio="none" style={{ display: "block" }}>
          <defs>
            <linearGradient id="wh-grad-pay" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#CD533B" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#CD533B" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={areaPath} fill="url(#wh-grad-pay)" opacity={animate ? 1 : 0} style={{ transition: "opacity 0.6s 0.4s" }} />
          <path d={linePath} fill="none" stroke="#CD533B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="300" strokeDashoffset={animate ? 0 : 300} style={{ transition: "stroke-dashoffset 1.1s ease-out" }} />
        </svg>
      </div>
    </div>
  );
}

const cards = [
  { title: "Listings & pricing", body: "Across 16+ platforms, repriced hourly. Dynamic pricing adjusts for events, seasonality and local demand.", icon: Tag, Graphic: GraphicPricing },
  { title: "Guests & security", body: "Every guest ID-verified, every message answered 7 days a week. Agreements signed, deposits held.", icon: ShieldCheck, Graphic: GraphicGuestsSecurity },
  { title: "Cleaning & housekeeping", body: "Professional turnover after every stay. Your property inspected, linens refreshed, ready for the next arrival.", icon: Sparkles, Graphic: GraphicCleaning },
  { title: "Payments & reporting", body: "Paid per booking within 48 hours. Full transparency on every payout, fee and deduction.", icon: Wallet, Graphic: GraphicPayments },
];

export default function WhatWeHandle() {
  const [ref, inView] = useInView({ threshold: 0.15 });

  return (
    <section ref={ref} style={{ backgroundColor: "#112215", padding: "clamp(60px,7vw,96px) clamp(20px,5vw,80px)" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#CCF5AC", marginBottom: 12, marginTop: 0 }}>Fully managed</p>
          <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(28px, 4vw, 44px)", letterSpacing: "-0.02em", lineHeight: 1.15, color: "#F4F1E7", margin: "0 auto 14px" }}>
            Everything handled, end to end
          </h2>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, color: "rgba(244,241,231,0.8)", lineHeight: 1.65, margin: "0 auto" }}>
            The only thing we need from you is the dates you want blocked.
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }} className="wh-grid">
          {cards.map(({ title, body, icon: Icon, Graphic }) => (
            <div
              key={title}
              style={{
                background: "#E8F2E0",
                borderRadius: 16,
                padding: "28px 24px 0",
                display: "flex",
                flexDirection: "column",
                minHeight: 300,
                overflow: "hidden",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 14px rgba(0,0,0,0.06)",
              }}
            >
              <Icon size={24} color="#112215" strokeWidth={1.8} style={{ marginBottom: 14 }} />
              <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 24, color: "#112215", margin: "0 0 8px", letterSpacing: "-0.01em", lineHeight: 1.2 }}>{title}</p>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: "#5C7268", lineHeight: 1.55, margin: 0, marginBottom: 18 }}>{body}</p>
              <Graphic animate={inView} />
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) { .wh-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 480px) { .wh-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </section>
  );
}